import * as Popover from '@radix-ui/react-popover'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'

import { ChevronDownIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { countries as countriesData } from './countries-data'

export interface Country {
  code: string
  name: string
  flag?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  dialCode: string
  flagFileName?: string
}

const flagModules = import.meta.glob<{ default: React.ComponentType<React.SVGProps<SVGSVGElement>> }>('/src/assets/icons/flags/*.svg', {
  query: '?react',
  eager: false,
})

const flagComponents = new Map<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>()

export const countries: Country[] = countriesData.map((country) => ({
  code: country.code,
  name: country.name,
  dialCode: country.dialCode,
  flagFileName: country.flagFileName,
  flag: undefined,
}))

interface CountrySelectorProps {
  value?: string
  phoneNumber?: string
  onCountryChange: (country: Country) => void
  className?: string
}

const detectCountryFromPhone = (phoneNumber: string): Country | null => {
  if (!phoneNumber) return null

  const normalized = phoneNumber.replace(/[\s\-()]/g, '')
  const sortedCountries = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length)

  for (const country of sortedCountries) {
    if (normalized.startsWith(country.dialCode)) {
      return country
    }
  }

  return null
}

const CountryFlag: React.FC<{ countryCode: string; width?: number; height?: number }> = ({ countryCode, width = 20, height = 15 }) => {
  const [FlagComponent, setFlagComponent] = useState<React.ComponentType<React.SVGProps<SVGSVGElement>> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const country = countriesData.find((c) => c.code === countryCode)
    if (!country) {
      setIsLoading(false)
      return
    }

    if (flagComponents.has(countryCode)) {
      const component = flagComponents.get(countryCode)
      if (component) {
        setFlagComponent(() => component)
        setIsLoading(false)
        return
      }
    }

    setIsLoading(true)
    const flagPath = `/src/assets/icons/flags/${country.flagFileName}`
    const loader = flagModules[flagPath]

    if (loader) {
      loader()
        .then((module) => {
          const component = module.default
          flagComponents.set(countryCode, component)
          setFlagComponent(() => component)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error(`Failed to load flag for ${countryCode}:`, error)
          setIsLoading(false)
        })
    } else {
      console.warn(`Flag not found: ${flagPath}`)
      setIsLoading(false)
    }
  }, [countryCode])

  if (isLoading || !FlagComponent) {
    return <div style={{ width, height, background: 'transparent' }} />
  }

  return <FlagComponent width={width} height={height} />
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ value, phoneNumber, onCountryChange, className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const countriesListRef = useRef<HTMLDivElement>(null)
  const getSelectedCountry = () => {
    if (value) {
      return countries.find((country) => country.code === value)
    }
    if (phoneNumber) {
      return detectCountryFromPhone(phoneNumber)
    }
    return null
  }

  const selectedCountry = getSelectedCountry() || countries[0]

  useEffect(() => {
    if (phoneNumber && !value) {
      const detectedCountry = detectCountryFromPhone(phoneNumber)
      if (detectedCountry && detectedCountry.code !== selectedCountry.code) {
        onCountryChange(detectedCountry)
      }
    }
  }, [phoneNumber, value, selectedCountry.code, onCountryChange])

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) || country.code.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (countriesListRef.current && isOpen) {
        e.stopPropagation()
        e.preventDefault()
        countriesListRef.current.scrollTop += e.deltaY
      }
    }

    if (isOpen && countriesListRef.current) {
      const listElement = countriesListRef.current
      listElement.addEventListener('wheel', handleWheel, { passive: false })

      return () => {
        listElement.removeEventListener('wheel', handleWheel)
      }
    }
  }, [isOpen])

  const handleCountrySelect = (country: Country) => {
    onCountryChange(country)
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button className={styles.flagButton} type="button">
            <div className={styles.flag}>
              <CountryFlag countryCode={selectedCountry.code} width={20} height={15} />
            </div>
            <ChevronDownIcon width={14} height={14} className={styles.chevron} />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={styles.popoverContent}
            align="start"
            sideOffset={4}
            onWheel={(e) => {
              e.stopPropagation()
            }}
          >
            <div className={styles.searchContainer}>
              <MagnifyingGlassIcon width={16} height={16} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div
              ref={countriesListRef}
              className={styles.countriesList}
              style={{
                overflowY: 'auto',
                maxHeight: '200px',
                pointerEvents: 'auto',
              }}
            >
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={`${styles.countryItem} ${selectedCountry.code === country.code ? styles.selected : ''}`}
                  type="button"
                >
                  <div className={styles.flag}>
                    <CountryFlag countryCode={country.code} width={20} height={15} />
                  </div>
                  <span className={styles.countryName}>{country.name}</span>
                </button>
              ))}

              {filteredCountries.length === 0 && <div className={styles.noResults}>No countries found</div>}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}

export default CountrySelector
